import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { ScrollableTabsDirective } from '../../scrollable-tabs.directive';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Patient, PatientFiscalData } from '../../models/patient.type';
import { fiscalFromCsf, normalizePatientFiscal } from '../../models/patient-fiscal';
import { RECIPIENT_RFC_RE, normalizeRecipientRfc } from '../../models/invoicing-recipient.type';
import { FiscalRegimeOption, fiscalRegimesForRfc } from '../../models/fiscal-regime.type';
import { CFDI_USE_OPTIONS } from '../../models/user-profile.type';
import {
  PatientFieldDef,
  PatientFieldType,
  getPatientFieldValue,
  setPatientFieldValue,
} from '../../models/patient-field.type';
import { FirestoreService } from '../../services/database/firestore.service';
import { InvoicingRecipientService } from '../../services/database/invoicing-recipient.service';
import { SupportingDocumentsComponent } from '../supporting-documents/supporting-documents.component';
import { PatientBillsComponent } from '../patient-bills/patient-bills.component';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { Timestamp } from 'firebase/firestore';
import {
  GenerateDocumentDialogComponent,
} from '../generate-document-dialog/generate-document-dialog.component';
import {
  CalendarEventService,
  doctorOptionLabel,
} from '../../services/database/calendar-event.service';
import { MembershipService } from '../../services/auth/membership.service';
import { UserProfileService } from '../../services/auth/user-profile.service';
import { formatMoney } from '../../models/billing-product.type';
import { formatDocumentDateLabel } from '../../utils/template-fill.util';
import { GuideAnchorDirective } from '../../guides/guide-anchor.directive';
import { OutlineFieldComponent } from '../outline-field/outline-field.component';
import { CallableFunctionsService, IssuedInvoice } from '../../services/functions/callable-functions.service';

export interface EditPatientDialogData {
  patient: Patient | undefined;
  updateResults: () => void;
  /** Prefill name when registering from a walk-in cita. */
  initialName?: string;
  /** Prefill email when the cita already has one. */
  initialEmail?: string;
  /** Opens Cuenta when the dialog is used from cuentas por cobrar. */
  initialTab?: 'cuenta';
}

/** Shared MatDialog size so the pane matches the card (backdrop clicks work on all sides). */
export const EDIT_PATIENT_DIALOG_OPTIONS = {
  width: '920px',
  maxWidth: '95vw',
  disableClose: true,
} as const;

type PatientForm = {
  name: FormControl<string>;
  dob: FormControl<Date | null>;
  fields: FormGroup<Record<string, FormControl<string>>>;
};

type FiscalForm = {
  rfc: FormControl<string>;
  name: FormControl<string>;
  fiscalRegime: FormControl<string>;
  postalCode: FormControl<string>;
  cfdiUse: FormControl<string>;
};

/** Month written out, so 1 de mayo de 2014 cannot be read as January 5. */
const BIRTH_DATE_FORMATS = {
  parse: { dateInput: { year: 'numeric', month: 'long', day: 'numeric' } },
  display: {
    dateInput: { year: 'numeric', month: 'long', day: 'numeric' },
    monthLabel: { month: 'short' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

const AUTOCOMPLETE_BY_KEY: Record<string, string> = {
  address: 'street-address',
  phone: 'tel',
  email: 'email',
};

const INPUT_TYPE_BY_FIELD_TYPE: Partial<Record<PatientFieldType, string>> = {
  text: 'text',
  date: 'date',
  number: 'number',
  phone: 'tel',
  email: 'email',
};

@Component({
  selector: 'app-edit-patient-dialog',
  templateUrl: './edit-patient-dialog.component.html',
  styleUrls: ['./edit-patient-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    provideNativeDateAdapter(BIRTH_DATE_FORMATS),
  ],
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatDatepickerModule,
    MatTabsModule,
    ScrollableTabsDirective,
    SupportingDocumentsComponent,
    PatientBillsComponent,
    GuideAnchorDirective,
    OutlineFieldComponent,
  ],
})
export class EditPatientDialogComponent {
  dialogRef = inject<MatDialogRef<EditPatientDialogComponent, Patient | undefined>>(MatDialogRef);
  data = inject<EditPatientDialogData>(MAT_DIALOG_DATA);
  private firestore = inject(FirestoreService);
  private recipients = inject(InvoicingRecipientService);
  private snack = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private calendar = inject(CalendarEventService);
  private membership = inject(MembershipService);
  private profiles = inject(UserProfileService);
  private flags = inject(FeatureFlagService);
  private callables = inject(CallableFunctionsService);

  readonly tabIndex = signal(this.data.initialTab === 'cuenta' ? 1 : 0);
  readonly cfdiUseOptions = CFDI_USE_OPTIONS;
  readonly fiscalForm = new FormGroup<FiscalForm>({
    rfc: new FormControl('', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    fiscalRegime: new FormControl('', { nonNullable: true }),
    postalCode: new FormControl('', { nonNullable: true }),
    cfdiUse: new FormControl('', { nonNullable: true }),
  });
  /** Birth dates stop at today. The year list ends on this year and scrolls back with no limit. */
  readonly dobMax = new Date();

  /** Clinic Facturación flag. Datos fiscales stays hidden when this is off. */
  readonly facturacionEnabled = computed(() => this.flags.isEnabled('invoicing'));
  readonly invoicingEnabled = computed(
    () =>
      this.facturacionEnabled() &&
      (this.membership.isSelectedBusinessSecretary() || this.membership.canAccessAppointments())
  );

  readonly isNewPatient = !this.data.patient;
  readonly patientId = signal<string | null>(this.data.patient?.id ?? null);
  readonly uploadsEnabled = signal(!this.isNewPatient);
  readonly generatingDocument = signal(false);
  readonly parsingCsf = signal(false);
  readonly saving = signal(false);
  /** RFC currently on the form, so the factura list follows edits and a constancia upload. */
  readonly fiscalRfc = signal('');
  readonly issued = signal<IssuedInvoice[]>([]);
  readonly invoicesLoading = signal(false);
  readonly invoicesError = signal('');
  readonly invoiceBusy = signal<number | null>(null);
  readonly invoiceAction = signal<'' | 'pdf' | 'email'>('');
  readonly patientInvoices = computed(() => {
    const rfc = normalizeRecipientRfc(this.fiscalRfc());
    if (!rfc) {
      return [];
    }
    return this.issued().filter((invoice) => normalizeRecipientRfc(invoice.rfc) === rfc);
  });
  private invoicesLoadedFor = '';
  private invoiceLoad = 0;
  private hasPersisted = !this.isNewPatient;
  private persistChain: Promise<void> = Promise.resolve();
  private persistGeneration = 0;
  private fiscalReady = false;
  private fiscalDirty = false;
  private fiscalTimer: ReturnType<typeof setTimeout> | null = null;
  private fiscalGeneration = 0;
  /** Snapshot of the clinic's visible fields when the dialog opened. */
  readonly fields: PatientFieldDef[] = this.membership
    .selectedPatientFields()
    .filter((field) => !field.hidden && !field.removed);
  public patientForm: FormGroup<PatientForm>;

  constructor() {
    const patient = this.data.patient;
    const initialName =
      patient?.name?.trim() || this.data.initialName?.trim() || '';
    const fieldControls: Record<string, FormControl<string>> = {};
    for (const field of this.fields) {
      if (field.type === 'spacer') {
        continue;
      }
      fieldControls[field.key] = new FormControl(getPatientFieldValue(patient, field), {
        nonNullable: true,
      });
    }
    const initialEmail = this.data.initialEmail?.trim() || '';
    if (initialEmail && fieldControls['email'] && !fieldControls['email'].value.trim()) {
      fieldControls['email'].setValue(initialEmail);
    }
    const storedPhone = typeof patient?.phone === 'string' ? patient.phone.trim() : '';
    const storedEmail = typeof patient?.email === 'string' ? patient.email.trim() : '';
    const contactMissing = (): boolean => {
      const phone = fieldControls['phone'] ? fieldControls['phone'].value.trim() : storedPhone;
      const email = fieldControls['email'] ? fieldControls['email'].value.trim() : storedEmail;
      return phone.length === 0 && email.length === 0;
    };
    const contactValidator = (): ValidationErrors | null =>
      contactMissing() ? { required: true } : null;
    fieldControls['phone']?.addValidators(contactValidator);
    fieldControls['email']?.addValidators(contactValidator);

    this.patientForm = new FormGroup({
      name: new FormControl(initialName, { nonNullable: true, validators: requiredTrimmed }),
      dob: new FormControl<Date | null>(patient?.dob ? patient.dob.toDate() : null, requiredDate),
      fields: new FormGroup(fieldControls),
    });

    const refreshContact = (): void => {
      fieldControls['phone']?.updateValueAndValidity({ emitEvent: false });
      fieldControls['email']?.updateValueAndValidity({ emitEvent: false });
    };
    fieldControls['phone']?.valueChanges.subscribe(refreshContact);
    fieldControls['email']?.valueChanges.subscribe(refreshContact);
    refreshContact();
    this.patientForm.controls.name.markAsTouched();
    this.patientForm.controls.dob.markAsTouched();
    fieldControls['phone']?.markAsTouched();
    fieldControls['email']?.markAsTouched();

    this.fiscalForm.setValue(normalizePatientFiscal(patient?.fiscal), { emitEvent: false });
    this.fiscalRfc.set(this.fiscalForm.controls.rfc.value);
    this.fiscalForm.controls.rfc.valueChanges.subscribe((rfc) => this.fiscalRfc.set(rfc));
    this.fiscalForm.valueChanges.subscribe(() => this.scheduleFiscalSave());
    this.fiscalReady = true;
    effect(() => {
      if (!this.facturacionEnabled()) {
        return;
      }
      const businessId = this.membership.selectedBusinessId();
      if (!businessId || businessId === this.invoicesLoadedFor) {
        return;
      }
      this.invoicesLoadedFor = businessId;
      void this.loadIssued(businessId);
    });
    inject(DestroyRef).onDestroy(() => {
      if (this.fiscalTimer) {
        clearTimeout(this.fiscalTimer);
      }
    });

    this.dialogRef.backdropClick().subscribe(() => this.dismissIfSaved());
    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        this.dismissIfSaved();
      }
    });
  }

  fiscalRegimeOptions(): FiscalRegimeOption[] {
    const options = fiscalRegimesForRfc(this.fiscalForm.controls.rfc.value);
    const current = this.fiscalForm.controls.fiscalRegime.value;
    if (current && !options.some((option) => option.code === current)) {
      return [{ code: current, name: 'Régimen guardado', natural: true, moral: true }, ...options];
    }
    return options;
  }

  async onConstancia(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file || this.parsingCsf() || !this.patientId()) {
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.snack.open('La constancia debe ser un PDF.', '', { duration: 3500 });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      this.snack.open('La constancia es demasiado grande.', '', { duration: 3500 });
      return;
    }
    this.parsingCsf.set(true);
    try {
      const fiscal = fiscalFromCsf(await this.callables.parseCsf(await readFileAsBase64(file)));
      if (!RECIPIENT_RFC_RE.test(fiscal.rfc)) {
        this.snack.open('No se pudo leer el RFC de la constancia.', '', { duration: 4000 });
        return;
      }
      this.fiscalForm.setValue(fiscal);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo leer la constancia.';
      this.snack.open(message, '', { duration: 4000 });
    } finally {
      this.parsingCsf.set(false);
    }
  }

  normalizeFiscalRfc(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rfc = normalizeRecipientRfc(input.value);
    if (rfc !== input.value) {
      this.fiscalForm.controls.rfc.setValue(rfc);
    }
  }

  normalizeFiscalPostal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const postal = input.value.replace(/\D/g, '').slice(0, 5);
    if (postal !== input.value) {
      this.fiscalForm.controls.postalCode.setValue(postal);
    }
  }

  inputType(field: PatientFieldDef): string {
    return INPUT_TYPE_BY_FIELD_TYPE[field.type] ?? 'text';
  }

  autocomplete(field: PatientFieldDef): string {
    return (field.builtIn && AUTOCOMPLETE_BY_KEY[field.key]) || 'off';
  }

  /** Keeps a stored value selectable even if the owner later removed that option. */
  selectOptions(field: PatientFieldDef): string[] {
    const options = field.options ?? [];
    const current = this.patientForm.controls.fields.controls[field.key]?.value;
    return current && !options.includes(current) ? [...options, current] : options;
  }

  /** Backdrop and Escape leave a saved patient. An unsaved one stays open. */
  dismissIfSaved(): void {
    if (!this.hasPersisted) {
      this.snack.open(
        'Guarde el paciente para cerrar, o use la X.',
        '',
        { duration: 3500 }
      );
      return;
    }
    this.closeDialog();
  }

  /** Always closes, including when the patient was never saved. */
  closeDialog(): void {
    this.dialogRef.close(this.hasPersisted ? this.data.patient : undefined);
  }

  async saveAndClose(): Promise<void> {
    if (!this.canPersist()) {
      this.snack.open(this.missingFieldsMessage(), '', { duration: 3500 });
      return;
    }
    this.saving.set(true);
    let saved = true;
    const generation = ++this.persistGeneration;
    this.persistChain = this.persistChain
      .then(() => this.persistPatient(generation))
      .catch(() => {
        saved = false;
      });
    await this.persistChain;
    this.saving.set(false);
    if (saved) {
      this.closeDialog();
    }
  }

  async openGenerateDocument(): Promise<void> {
    if (!this.patientId() || !this.data.patient) {
      this.snack.open(
        'Guarde el paciente antes de generar un documento.',
        '',
        { duration: 3500 }
      );
      return;
    }

    const patient = this.patientFromFormControl();
    this.generatingDocument.set(true);
    try {
      const doctors = await this.calendar.listActiveDoctors();
      if (doctors.length === 0) {
        this.snack.open('No hay doctores activos en este consultorio.', '', {
          duration: 4000,
        });
        return;
      }

      this.dialog.open(GenerateDocumentDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        data: {
          patient,
          dateLabel: formatDocumentDateLabel(),
          doctorLabel: this.membership.canAccessAppointments()
            ? this.profiles.doctorLabel()
            : undefined,
          doctors: doctors.map((doctor) => ({
            uid: doctor.uid,
            label: doctorOptionLabel(doctor),
          })),
        },
      });
    } catch (error) {
      console.error(error);
      this.snack.open('No se pudo abrir la generación de documentos.', '', {
        duration: 4000,
      });
    } finally {
      this.generatingDocument.set(false);
    }
  }

  private canPersist(): boolean {
    const name = this.patientForm.controls.name.value.trim();
    const dob = this.patientForm.controls.dob.value;
    return (
      name.length > 0 &&
      this.hasPhoneOrEmail() &&
      dob instanceof Date &&
      !Number.isNaN(dob.getTime())
    );
  }

  private hasPhoneOrEmail(): boolean {
    return this.contactText('phone').length > 0 || this.contactText('email').length > 0;
  }

  /** The form value when the field is visible; otherwise the value already stored. */
  private contactText(key: 'phone' | 'email'): string {
    const control = this.patientForm.controls.fields.controls[key];
    if (control) {
      return control.value.trim();
    }
    const stored = this.data.patient?.[key];
    return typeof stored === 'string' ? stored.trim() : '';
  }

  private missingFieldsMessage(): string {
    return 'Nombre, fecha de nacimiento y teléfono o correo son obligatorios para guardar.';
  }

  patientFromFormControl(): Patient {
    if (!this.data.patient) {
      this.data.patient = this.firestore.createBlankPatient();
    }
    const currPatient = this.data.patient;
    currPatient.name = this.patientForm.controls.name.value.trim();
    const dob = this.patientForm.controls.dob.value;
    if (dob) {
      currPatient.dob = Timestamp.fromDate(new Date(dob.getFullYear(), dob.getMonth(), dob.getDate()));
    }
    // Hidden fields are not in the form, so their stored values are left untouched.
    const values = this.patientForm.controls.fields.getRawValue();
    for (const field of this.fields) {
      if (field.type === 'spacer') {
        continue;
      }
      setPatientFieldValue(currPatient, field, values[field.key] ?? '');
    }

    return currPatient;
  }

  formChange() {
    if (!this.canPersist()) {
      if (this.isNewPatient && !this.hasPersisted) {
        return;
      }
      this.snack.open(this.missingFieldsMessage(), '', { duration: 3500 });
      return;
    }

    // Queue saves so rapid field edits don't race createPatient.
    const generation = ++this.persistGeneration;
    this.persistChain = this.persistChain
      .then(() => this.persistPatient(generation))
      .catch(() => undefined);
  }

  private async persistPatient(generation: number): Promise<void> {
    const patient = this.patientFromFormControl();
    try {
      await this.firestore.updatePatient(patient);
      this.hasPersisted = true;
      this.patientId.set(patient.id ?? null);
      if (this.facturacionEnabled() && this.fiscalDirty && patient.id) {
        await this.persistFiscal(patient.id, false);
      }
      this.uploadsEnabled.set(true);
      this.data.updateResults();
      // Only toast for the latest save in a burst of field changes.
      if (generation === this.persistGeneration) {
        this.snack.open('Los cambios se guardaron exitosamente!', '', {
          duration: 2000,
        });
      }
    } catch (err) {
      console.error(err);
      const message =
        err instanceof Error ? err.message : 'No se pudo guardar el paciente';
      this.snack.open(message, '', { duration: 4500 });
      throw err;
    }
  }

  private scheduleFiscalSave(): void {
    if (!this.fiscalReady || !this.facturacionEnabled()) {
      return;
    }
    this.fiscalDirty = true;
    if (this.fiscalTimer) {
      clearTimeout(this.fiscalTimer);
    }
    this.fiscalTimer = setTimeout(() => {
      this.fiscalTimer = null;
      const id = this.patientId();
      if (!id) {
        return;
      }
      void this.persistFiscal(id, true);
    }, 400);
  }

  private async persistFiscal(patientId: string, notify: boolean): Promise<void> {
    const generation = ++this.fiscalGeneration;
    const fiscal = this.fiscalPayload();
    try {
      await this.firestore.setPatientFiscal(patientId, fiscal);
      if (generation !== this.fiscalGeneration) {
        return;
      }
      if (this.data.patient) {
        this.data.patient.fiscal = fiscal;
      }
      try {
        await this.recipients.syncFromPatient(patientId, this.patientForm.controls.name.value.trim(), fiscal);
      } catch (syncErr) {
        console.error(syncErr);
        const syncMessage = syncErr instanceof Error ? syncErr.message : 'No se pudo actualizar el receptor SAT.';
        this.snack.open(syncMessage, '', { duration: 4500 });
        return;
      }
      if (generation !== this.fiscalGeneration) {
        return;
      }
      this.fiscalDirty = false;
      if (notify) {
        this.snack.open('Datos fiscales guardados.', '', { duration: 2000 });
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'No se pudieron guardar los datos fiscales';
      this.snack.open(message, '', { duration: 4500 });
      throw err;
    }
  }

  patientEmail(): string {
    return this.contactText('email');
  }

  async downloadInvoice(invoice: IssuedInvoice): Promise<void> {
    if (this.invoiceBusy() != null || !invoice.folio) {
      return;
    }
    const businessId = this.membership.selectedBusinessId();
    if (!businessId) {
      this.snack.open('Selecciona un consultorio.', '', { duration: 3000 });
      return;
    }
    this.invoiceBusy.set(invoice.id);
    this.invoiceAction.set('pdf');
    try {
      const file = await this.callables.downloadInvoicingCfdiPdf(businessId, invoice.id);
      savePdf(file.filename || `${this.invoiceFolio(invoice)}.pdf`, file.pdfBase64);
    } catch (err) {
      console.error(err);
      this.snack.open(invoiceMessage(err, 'No se pudo descargar el PDF.'), '', { duration: 4000 });
    } finally {
      this.invoiceBusy.set(null);
      this.invoiceAction.set('');
    }
  }

  async emailInvoice(invoice: IssuedInvoice): Promise<void> {
    if (this.invoiceBusy() != null || invoice.cancelled || !invoice.folio) {
      return;
    }
    const recipient = this.patientEmail();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      this.snack.open('Agrega el correo del paciente para enviar la factura.', '', { duration: 3500 });
      return;
    }
    const businessId = this.membership.selectedBusinessId();
    if (!businessId) {
      this.snack.open('Selecciona un consultorio.', '', { duration: 3000 });
      return;
    }
    this.invoiceBusy.set(invoice.id);
    this.invoiceAction.set('email');
    try {
      await this.callables.sendInvoicingCfdiEmail(businessId, invoice.id, recipient);
      this.snack.open(`Correo enviado a ${recipient}`, '', { duration: 2500 });
    } catch (err) {
      console.error(err);
      this.snack.open(invoiceMessage(err, 'No se pudo enviar el correo.'), '', { duration: 4000 });
    } finally {
      this.invoiceBusy.set(null);
      this.invoiceAction.set('');
    }
  }

  invoiceFolio(invoice: IssuedInvoice): string {
    if (invoice.serie && invoice.folio) {
      return `${invoice.serie}-${invoice.folio}`;
    }
    return invoice.folio || invoice.satUuid || 'Factura';
  }

  invoiceWhen(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(date);
  }

  invoiceMoney(value: string): string {
    const amount = Number(value);
    return Number.isFinite(amount) ? formatMoney(amount) : '—';
  }

  invoiceType(value: string): string {
    switch (value.trim().toUpperCase()) {
      case 'I':
        return 'Ingreso';
      case 'E':
        return 'Egreso';
      case 'P':
        return 'Pago';
      default:
        return '';
    }
  }

  private async loadIssued(businessId: string): Promise<void> {
    const generation = ++this.invoiceLoad;
    this.invoicesLoading.set(true);
    this.invoicesError.set('');
    try {
      const page = await this.callables.listInvoicingCfdis(businessId, 100);
      if (generation !== this.invoiceLoad) {
        return;
      }
      this.issued.set(page.items || []);
    } catch (err) {
      if (generation !== this.invoiceLoad) {
        return;
      }
      console.error(err);
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las facturas.';
      this.invoicesError.set(message);
    } finally {
      if (generation === this.invoiceLoad) {
        this.invoicesLoading.set(false);
      }
    }
  }

  private fiscalPayload(): PatientFiscalData {
    return normalizePatientFiscal(this.fiscalForm.getRawValue());
  }
}

function requiredTrimmed(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() ? null : { required: true };
}

function requiredDate(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  return value instanceof Date && !Number.isNaN(value.getTime()) ? null : { required: true };
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl);
    };
    reader.onerror = () => reject(new Error('No se pudo leer la constancia.'));
    reader.readAsDataURL(file);
  });
}

function savePdf(filename: string, pdfBase64: string): void {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function invoiceMessage(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message.replace(/\s*\(functions\/[^)]+\)\s*$/, '') : '';
  return message || fallback;
}
